/**
 * Opposition case entity (W10).
 *
 * A case is the dossier a lawyer opens on one conflict: one of the firm's own
 * trademarks (the *brand*) against one filing (the *mark*), with a lifecycle,
 * deadlines, notes and files. The API calls it a *procedure*; the product
 * says *opposition case*. `oppositions.service.ts` is the only file that knows
 * both words.
 *
 * Two rules shape the fields below, and both are the API's own:
 *
 * - **A case says where its conflict came from.** Opened from a match it
 *   carries that match — risk band, who decided it, the verdict's words.
 *   Opened by hand it has no match, and `risk` is **null**, not "low": a
 *   dossier with no machine reading behind it has no risk score, and printing
 *   the bottom of the scale would be a claim. Client viewers never see a
 *   match either (the matches policy has no viewer branch).
 * - **A deadline says how sure it is.** `provisional` is true where the date
 *   comes from a registry rule the firm's lawyers have not confirmed (every
 *   WIPO date, EUIPO M.1 — D-037). The page must say so beside the date.
 */
import type { RawRecord, RiskLevel } from './raw'

export type CaseStatus =
  | 'under_review'
  | 'drafting'
  | 'filed'
  | 'awaiting_response'
  | 'negotiation'
  | 'resolved'
  | 'closed'

export type CasePriority = 'low' | 'medium' | 'high' | 'urgent'
export type CaseOutcome = 'won' | 'lost' | 'settled' | 'withdrawn'
export type NoteCategory = 'legal' | 'internal' | 'status'
export type DocumentCategory =
  | 'filing'
  | 'evidence'
  | 'correspondence'
  | 'decision'
  | 'agreement'
  | 'other'
export type DeadlineKind = 'statutory' | 'internal'

/** Every status, in lifecycle order — the stepper and the filter read this. */
export const CASE_STATUSES: readonly CaseStatus[] = [
  'under_review',
  'drafting',
  'filed',
  'awaiting_response',
  'negotiation',
  'resolved',
  'closed',
]

/** A case is active until it is resolved or closed (the API's own rule). */
export const ACTIVE_STATUSES: readonly CaseStatus[] = [
  'under_review',
  'drafting',
  'filed',
  'awaiting_response',
  'negotiation',
]

export const CASE_PRIORITIES: readonly CasePriority[] = ['urgent', 'high', 'medium', 'low']
export const CASE_OUTCOMES: readonly CaseOutcome[] = ['won', 'settled', 'lost', 'withdrawn']
export const NOTE_CATEGORIES: readonly NoteCategory[] = ['legal', 'internal', 'status']
export const DOCUMENT_CATEGORIES: readonly DocumentCategory[] = [
  'filing',
  'evidence',
  'correspondence',
  'decision',
  'agreement',
  'other',
]

export interface CasePerson {
  id: number
  name: string
  initials: string
}

/** The protected side: the client's own trademark. */
export interface CaseBrand {
  id: number
  title: string
  applicationNumber: string | null
  registrationDate: string | null
  markType: string | null
  country: string | null
  classes: number[]
  hasLogo: boolean
}

/** The opposed side. `id` is null for a conflict typed in by hand. */
export interface CaseMark {
  id: number | null
  title: string | null
  applicationNumber: string | null
  owner: string | null
  markType: string | null
  applicationDate: string | null
  classes: number[]
  /** The registry's own opposition deadline on the found mark, if any. */
  deadline: string | null
  hasLogo: boolean
}

/** The match the case was opened from, as the monitoring page shows it. */
export interface CaseMatch {
  id: number
  risk: RiskLevel
  /** A judge's probability and a belt's similarity are different numbers. */
  source: 'judge' | 'rules'
  verdict: string | null
  score: number
  justification: string | null
}

export interface CaseDeadline {
  id: number
  title: string
  dueDate: string
  kind: DeadlineKind
  source: 'manual' | 'mark' | 'rule'
  /** The date follows a registry rule the lawyers have not confirmed (D-037). */
  provisional: boolean
  note: string | null
  doneAt: string | null
  /** Negative once passed; null once done. Counted on the server's clock. */
  daysLeft: number | null
}

export interface OppositionCase {
  id: number
  title: string
  caseNumber: string | null
  status: CaseStatus
  /** Resolved or closed cases are finished; everything else is active. */
  active: boolean
  priority: CasePriority
  outcome: CaseOutcome | null
  grounds: string | null
  /** A feed code — EUIPO, WIPO, BG — or null for a hand-opened case with none. */
  registry: string | null
  client: { id: number; name: string }
  brand: CaseBrand
  mark: CaseMark
  match: CaseMatch | null
  /** From the match. Null when there is none — never defaulted to low. */
  risk: RiskLevel | null
  lawyer: CasePerson | null
  filingDate: string | null
  nextDeadline: CaseDeadline | null
  documents: number
  notes: number
  lastActivityAt: string | null
  createdAt: string | null
  updatedAt: string | null
  closedAt: string | null
  raw: RawRecord
}

/** The dossier: the list row plus what only the case page needs. */
export interface OppositionCaseDetail extends OppositionCase {
  /** The firm's working notes; null for a client viewer. */
  internalNotes: string | null
  deadlines: CaseDeadline[]
  /** Where the case may go next. Empty for anyone who cannot edit. */
  transitions: CaseStatus[]
  createdBy: CasePerson | null
  canEdit: boolean
}

export function initialsOf(name: string | null | undefined): string {
  return (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toUpperCase()
}

function person(raw: RawRecord | null | undefined): CasePerson | null {
  if (!raw || raw.id == null) return null
  const name = raw.name ?? ''
  return { id: raw.id, name, initials: raw.initials ?? initialsOf(name) }
}

export function createDeadline(raw: RawRecord = {}): CaseDeadline {
  return {
    id: raw.id,
    title: raw.title ?? '',
    dueDate: raw.dueDate ?? '',
    kind: raw.kind ?? 'internal',
    source: raw.source ?? 'manual',
    provisional: Boolean(raw.provisional),
    note: raw.note ?? null,
    doneAt: raw.doneAt ?? null,
    daysLeft: raw.daysLeft ?? null,
  }
}

/**
 * The factory's input is the *already adapted* record (camelCase), not the
 * API payload — `raw` keeps it for debugging, and anything a component needs
 * is a named field here. Reading `raw.<api_field>` in a template is how a
 * page ends up rendering "undefined" beside a real value.
 */
export function createOpposition(raw: RawRecord = {}): OppositionCase {
  const status: CaseStatus = raw.status ?? 'under_review'
  const match: CaseMatch | null = raw.match ?? null
  const brand = raw.brand ?? {}
  const mark = raw.mark ?? {}
  return {
    id: raw.id,
    title: raw.title ?? '',
    caseNumber: raw.caseNumber ?? null,
    status,
    active: ACTIVE_STATUSES.includes(status),
    priority: raw.priority ?? 'medium',
    outcome: raw.outcome ?? null,
    grounds: raw.grounds ?? null,
    registry: raw.registry ?? null,
    client: { id: raw.client?.id ?? 0, name: raw.client?.name ?? '' },
    brand: {
      id: brand.id,
      title: brand.title ?? '',
      applicationNumber: brand.applicationNumber ?? null,
      registrationDate: brand.registrationDate ?? null,
      markType: brand.markType ?? null,
      country: brand.country ?? null,
      classes: Array.isArray(brand.classes) ? brand.classes : [],
      hasLogo: Boolean(brand.hasLogo),
    },
    mark: {
      id: mark.id ?? null,
      title: mark.title ?? null,
      applicationNumber: mark.applicationNumber ?? null,
      owner: mark.owner ?? null,
      markType: mark.markType ?? null,
      applicationDate: mark.applicationDate ?? null,
      classes: Array.isArray(mark.classes) ? mark.classes : [],
      deadline: mark.deadline ?? null,
      hasLogo: Boolean(mark.hasLogo),
    },
    match,
    risk: match?.risk ?? null,
    lawyer: person(raw.lawyer),
    filingDate: raw.filingDate ?? null,
    nextDeadline: raw.nextDeadline ? createDeadline(raw.nextDeadline) : null,
    documents: raw.documents ?? 0,
    notes: raw.notes ?? 0,
    lastActivityAt: raw.lastActivityAt ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    closedAt: raw.closedAt ?? null,
    raw,
  }
}

export function createOppositionDetail(raw: RawRecord = {}): OppositionCaseDetail {
  return {
    ...createOpposition(raw),
    internalNotes: raw.internalNotes ?? null,
    deadlines: Array.isArray(raw.deadlines) ? raw.deadlines.map(createDeadline) : [],
    transitions: Array.isArray(raw.transitions) ? raw.transitions : [],
    createdBy: person(raw.createdBy),
    canEdit: Boolean(raw.canEdit),
  }
}

export function createOppositionList(rawList: RawRecord[] = []): OppositionCase[] {
  return rawList.map(createOpposition)
}
